import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { computeTextDiff, type DiffSegment } from './diffUtils'

interface TextDiffProps {
  /** Array of diff segments from computeTextDiff */
  segments: DiffSegment[]
  /** Whether to show in a code-like monospace font */
  monospace?: boolean
}

/**
 * Renders text diff with highlighting for insertions and deletions
 *
 * - Insertions are shown with green background
 * - Deletions are shown with red background and strikethrough
 * - Equal parts are shown normally
 */
export function TextDiff({ segments, monospace = false }: TextDiffProps) {
  const fontClass = monospace ? 'font-mono text-sm' : ''

  return (
    <div className={`whitespace-pre-wrap break-words ${fontClass}`}>
      {segments.map((segment, index) => {
        if (segment.type === 'equal') {
          return (
            <span key={index} className="text-gray-900 dark:text-gray-100">
              {segment.text}
            </span>
          )
        }

        if (segment.type === 'insert') {
          return (
            <span
              key={index}
              className="rounded-sm bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200"
            >
              {segment.text}
            </span>
          )
        }

        if (segment.type === 'delete') {
          return (
            <span
              key={index}
              className="rounded-sm bg-red-100 text-red-800 line-through dark:bg-red-900/40 dark:text-red-200"
            >
              {segment.text}
            </span>
          )
        }

        return null
      })}
    </div>
  )
}

interface SideBySideDiffProps {
  /** The previous (before) value as string */
  previousValue: string
  /** The current (after) value as string */
  currentValue: string
  /** Whether to show in a code-like monospace font */
  monospace?: boolean
  /**
   * Pre-computed diff segments. When provided, only the changed parts are
   * highlighted within each column instead of marking the whole value.
   * Falls back to computing the diff from the two values when omitted.
   */
  segments?: DiffSegment[]
}

/**
 * Renders one column of a side-by-side diff with inline highlighting.
 *
 * - On the "before" side, only `delete` segments are highlighted (inserts are hidden)
 * - On the "after" side, only `insert` segments are highlighted (deletes are hidden)
 * - `equal` segments are shown as plain context on both sides
 */
function DiffColumn({
  segments,
  side,
  fontClass,
}: {
  segments: DiffSegment[]
  side: 'before' | 'after'
  fontClass: string
}) {
  const highlightType = side === 'before' ? 'delete' : 'insert'
  const hiddenType = side === 'before' ? 'insert' : 'delete'

  const highlightClass =
    side === 'before'
      ? 'rounded-sm bg-red-100 text-red-800 line-through dark:bg-red-900/40 dark:text-red-200'
      : 'rounded-sm bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200'

  return (
    <div
      className={`whitespace-pre-wrap break-words ${fontClass} text-gray-800 dark:text-gray-200`}
    >
      {segments.map((segment, index) => {
        if (segment.type === hiddenType) {
          return null
        }
        if (segment.type === highlightType) {
          return (
            <span key={index} className={highlightClass}>
              {segment.text}
            </span>
          )
        }
        return <span key={index}>{segment.text}</span>
      })}
    </div>
  )
}

/**
 * Renders a side-by-side comparison of two values
 * Shows previous value on the left and current value on the right
 *
 * Only the parts that actually changed are highlighted (deletions in red on the
 * left, insertions in green on the right); unchanged context is shown normally.
 */
export function SideBySideDiff({
  previousValue,
  currentValue,
  monospace = false,
  segments,
}: SideBySideDiffProps) {
  const { t } = useTranslation()
  const fontClass = monospace ? 'font-mono text-sm' : 'text-sm'

  const computedSegments = useMemo(
    () => computeTextDiff(previousValue, currentValue),
    [previousValue, currentValue]
  )
  const diffSegments = segments ?? computedSegments
  const emptyPlaceholder = (
    <span className="italic text-gray-400">
      {t('resources.assistants.revisions.comparison.empty')}
    </span>
  )

  return (
    <div className="grid grid-cols-2 gap-4">
      {/* Previous (Before) */}
      <div className="rounded-lg border border-red-200 bg-red-50/50 p-4 dark:border-red-900/50 dark:bg-red-950/20">
        <div className="mb-2 text-xs font-medium uppercase tracking-wide text-red-600 dark:text-red-400">
          {t('resources.assistants.revisions.comparison.before')}
        </div>
        {previousValue ? (
          <DiffColumn segments={diffSegments} side="before" fontClass={fontClass} />
        ) : (
          <div className={`whitespace-pre-wrap break-words ${fontClass}`}>
            {emptyPlaceholder}
          </div>
        )}
      </div>

      {/* Current (After) */}
      <div className="rounded-lg border border-green-200 bg-green-50/50 p-4 dark:border-green-900/50 dark:bg-green-950/20">
        <div className="mb-2 text-xs font-medium uppercase tracking-wide text-green-600 dark:text-green-400">
          {t('resources.assistants.revisions.comparison.after')}
        </div>
        {currentValue ? (
          <DiffColumn segments={diffSegments} side="after" fontClass={fontClass} />
        ) : (
          <div className={`whitespace-pre-wrap break-words ${fontClass}`}>
            {emptyPlaceholder}
          </div>
        )}
      </div>
    </div>
  )
}

export default TextDiff
